import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { PageHeader } from '@/components/core';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { DEFAULT_MY_TIMESHEETS_ORDER } from '../../graphql/queries';
import { createTimesheetTableColumns } from '../../components/timesheet-table-columns/timesheet-table-columns';
import { TimesheetTaskFormDialog } from '../../components/timesheet-task-form-dialog/timesheet-task-form-dialog';
import {
  useCurrentUserId,
  useDeleteTimeSheetEntry,
  useGetMyTimeSheets,
} from '../../hooks/use-my-timesheet';
import { TimeSheetRecord, TimeSheetsPagingResult } from '../../types/timesheet.types';
import { orgToday, startOfMonthDay, taskDateRangeClause } from '../../../utils/task-date';

export const MyTimesheetPage = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();

  const [dateRange, setDateRange] = useState(() => {
    const today = orgToday();
    return { from: startOfMonthDay(today), to: today };
  });
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_MY_TIMESHEETS_ORDER,
  });
  const [formState, setFormState] = useState<{ open: boolean; timeSheet: TimeSheetRecord | null }>({
    open: false,
    timeSheet: null,
  });
  const [deleteTarget, setDeleteTarget] = useState<TimeSheetRecord | null>(null);

  const filter = {
    UserId: userId,
    ...(dateRange.from && dateRange.to
      ? { TaskDate: taskDateRangeClause(dateRange.from, dateRange.to) }
      : {}),
  };

  // A new date range starts over from the first page.
  useEffect(() => {
    resetPage();
  }, [dateRange, resetPage]);

  const { data, isLoading, error } = useGetMyTimeSheets({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { TimeSheets?: TimeSheetsPagingResult })?.TimeSheets;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteEntry, isPending: isDeleting } = useDeleteTimeSheetEntry();

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteEntry(deleteTarget.ItemId);
    setDeleteTarget(null);
  };

  const columns = createTimesheetTableColumns({
    t,
    onEdit: (record) => setFormState({ open: true, timeSheet: record }),
    onDelete: setDeleteTarget,
  });

  const dateRangePicker = (
    <div className="flex items-center gap-3">
      <DatePicker
        value={dateRange.from}
        onChange={(value) => setDateRange((prev) => ({ ...prev, from: value }))}
        className="min-w-0 flex-1 md:w-40 md:flex-none"
      />
      <span className="shrink-0 text-sm text-neutral-500">{t('MY_TIMESHEET_TO')}</span>
      <DatePicker
        value={dateRange.to}
        onChange={(value) => setDateRange((prev) => ({ ...prev, to: value }))}
        className="min-w-0 flex-1 md:w-40 md:flex-none"
      />
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-4">
      {/* The date range sits beside the title from a tablet up; on a phone it has its own full-width
          line under it, as the old portal's week filter. */}
      <PageHeader
        title={t('MY_TIMESHEET')}
        actions={[
          {
            label: t('MY_TIMESHEET_ADD_TASK'),
            icon: <Plus className="size-5" />,
            onClick: () => setFormState({ open: true, timeSheet: null }),
          },
        ]}
      >
        <div className="max-md:hidden">{dateRangePicker}</div>
      </PageHeader>
      <div className="md:hidden">{dateRangePicker}</div>

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <TimesheetTaskFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, timeSheet: open ? prev.timeSheet : null }))
        }
        timeSheet={formState.timeSheet}
      />

      <ConfirmationModal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('MY_TIMESHEET_DELETE_ENTRY')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        confirmClassName="bg-destructive hover:bg-destructive/90"
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
